import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {semanticColor} from "@khanacademy/wonder-blocks-tokens";
import {
    mediaQueries,
    topBarHeightDesktop,
    topBarHeightMobile,
} from "@khan/shared-styles";

/**
 * We need to be smart here and only create a new Loadable once rather than
 * every render. So let's cache the first one we make then reuse it.
 */
const CreateAssignmentModal = React.lazy(
    () => import("./create-assignment-modal.tsx"),
);

interface Props {
    contentDescriptors: string[];
    classroomDescriptor: string;
    studentKaids: Array<string>;
    onSuccess?: () => unknown;
    onClose?: () => unknown;
}

/*
 * This component is used to asynchronously load the Assignment Modal with
 * specific Content Descriptors and Classroom Descriptor / Student Kaids used
 * for preselecting the Assignees.
 *
 * The component hierarchy between this and AssignmentModal is as follows:
 *
 * 1. `AsyncCreateAssignmentModal`: Injects a dynamic import of
 *    `CreateAssignmentModalProvider` into `LoadableCreateAssignmentModalComponent`
 *    using the `LoadableComponent` from the `react-loadable` package. Has props
 *    `contentDescriptors`, `classroomDescriptor`, and `studentKaids`. These will
 *    be passed down through each level of the component hierarchy.
 * 2. `CreateAssignmentModalProvider`: A simple component that renders two other
 *    child components. It wraps the `CreateAssignmentModalLauncher` component
 *    with the `StoreProvider` component from the package `class-package`. This
 *    wrapping of `CreateAssignmentModalLauncher` with `StoreProvider` component
 *    is required so the that the `CreateAssignmentModalLauncher` can start to
 *    set up the specific redux store that `CreateAssignmentModalLauncher` from
 *    `classroom-package` package offers.
 * 3. `CreateAssignmentModalLauncher`: The `CreateAssignmentModalLauncher`
 *    component calls `createStore` to instantiate a redux store provided by
 *    parent `StoreProvider` component and dispatches the `newAssignment` action
 *    containing the inherited props `contentDescriptors`, `classroomDescriptor`,
 *    and `studentKaids`. WIth the redux store all set up and containing the
 *    context of the new assignment, `CreateAssignmentModalLauncher` renders
 *    `AssignmentModal` wrapped with the `ModalLauncher` component from Wonder
 *    Blocks. Because `ModalLauncher`'s `opened` prop is hardcoded to `true`, it
 *    opens right away. At this point the teacher should see the Assignment Modal
 *    with the classroom, students, and content passed through by props now
 *    preselected in the Assignments Modal.
 */
const AsyncCreateAssignmentModal = ({
    contentDescriptors,
    classroomDescriptor,
    studentKaids,
    onSuccess,
    onClose,
}: Props): React.ReactElement => {
    return (
        <React.Suspense
            fallback={
                <div className={css(styles.assignmentLoadingIndicator)}>
                    <CircularSpinner size="xsmall" light={true} />
                </div>
            }
        >
            <CreateAssignmentModal
                contentDescriptors={contentDescriptors}
                classroomDescriptor={classroomDescriptor}
                studentKaids={studentKaids}
                onSuccess={onSuccess}
                onClose={onClose}
            />
        </React.Suspense>
    );
};

const styles = StyleSheet.create({
    assignmentLoadingIndicator: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: topBarHeightDesktop,
        backgroundColor: semanticColor.surface.inverse,

        [mediaQueries.smOrSmaller]: {
            height: topBarHeightMobile,
        },
    },
});

export default AsyncCreateAssignmentModal;
